/**
 * CSS Variable Color Resolver
 *
 * Utility functions for resolving CSS variables to their computed color values.
 * This is necessary for chart libraries like Recharts that don't support CSS variables directly.
 */

/**
 * Check if a color value is a CSS variable
 *
 * @param color - Color value to check
 * @returns True if the color is a CSS variable format
 */
export function isCSSVariable(color: string | undefined): boolean {
  if (!color || typeof color !== 'string') {
    return false;
  }
  return color.trim().startsWith('var(');
}

/**
 * Parse a CSS variable string to extract the variable name and fallback
 *
 * @param cssVar - CSS variable string (e.g., "var(--color-primary, #10b981)")
 * @returns Object with variableName and fallback, or null if invalid
 */
export function parseCSSVariable(cssVar: string): {
  variableName: string;
  fallback?: string;
} | null {
  if (!isCSSVariable(cssVar)) {
    return null;
  }

  // Match: var(--variable-name, fallback) or var(--variable-name)
  // Updated regex to handle nested var() calls correctly
  // Strategy: Match the variable name, then if there's a comma, capture everything until the LAST closing paren
  const trimmed = cssVar.trim();

  // Find the opening parenthesis of 'var('
  const varStart = trimmed.indexOf('var(');
  if (varStart === -1) return null;

  // Extract content inside var(...)
  const contentStart = varStart + 4; // After 'var('
  let parenDepth = 1;
  let contentEnd = contentStart;

  // Find the matching closing paren, accounting for nested parens
  for (let i = contentStart; i < trimmed.length && parenDepth > 0; i++) {
    if (trimmed[i] === '(') parenDepth++;
    else if (trimmed[i] === ')') {
      parenDepth--;
      if (parenDepth === 0) {
        contentEnd = i;
        break;
      }
    }
  }

  if (parenDepth !== 0) return null; // Unbalanced parentheses

  const content = trimmed.substring(contentStart, contentEnd).trim();

  // Split on the first comma (if any) to separate variable name and fallback
  const commaIndex = content.indexOf(',');

  if (commaIndex === -1) {
    // No fallback
    return {
      variableName: content.trim(),
    };
  }

  return {
    variableName: content.substring(0, commaIndex).trim(),
    fallback: content.substring(commaIndex + 1).trim(),
  };
}

/**
 * Find the nearest ancestor element with [data-palette-scope="true"]
 *
 * @param startElement - Element to start searching from
 * @returns The nearest palette scope element, or null if not found
 */
export function findNearestPaletteScope(startElement: HTMLElement | null): HTMLElement | null {
  if (!startElement) {
    return null;
  }

  let currentElement: HTMLElement | null = startElement;
  let depth = 0;

  // Traverse up the DOM tree looking for data-palette-scope="true"
  while (currentElement && depth < 50) { // Safety limit
    if (currentElement.dataset?.paletteScope === 'true') {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
    depth++;
  }

  return null;
}

/**
 * Resolve a CSS variable to its computed color value
 *
 * @param color - Color value (can be CSS variable or regular color)
 * @param element - Optional element to use for computing styles (defaults to document.documentElement)
 * @returns Resolved color value
 */
export function resolveCSSVariable(
  color: string | undefined,
  element: HTMLElement | null | undefined = document.documentElement
): string {
  if (!color) {
    return '';
  }

  // If it's not a CSS variable, return as-is
  if (!isCSSVariable(color)) {
    return color;
  }

  // Parse the CSS variable
  const parsed = parseCSSVariable(color);
  if (!parsed) {
    console.warn('[cssVariableResolver] Failed to parse CSS variable:', color);
    return color; // Return original if parsing fails
  }

  // Determine the element to query
  let queryElement = element || document.documentElement;

  // Get computed value from the element
  try {
    let computedValue = getComputedStyle(queryElement)
      .getPropertyValue(parsed.variableName)
      .trim();

    // If no value found on the provided element, try to find palette scope
    if (!computedValue) {
      // Always try to find palette scope when variable not found
      const paletteScope = findNearestPaletteScope(queryElement);

      if (paletteScope) {
        computedValue = getComputedStyle(paletteScope)
          .getPropertyValue(parsed.variableName)
          .trim();
      }
    }

    // If computed value exists, return it
    if (computedValue) {
      return computedValue;
    }

    // Otherwise, return fallback if available
    if (parsed.fallback) {
      // Recursively resolve in case fallback is also a CSS variable
      return resolveCSSVariable(parsed.fallback, queryElement);
    }

    // No value and no fallback - return original (this is expected during palette loading)
    return color;
  } catch (error) {
    console.error('[cssVariableResolver] Error resolving CSS variable:', error);
    return parsed.fallback || color;
  }
}

/**
 * Resolve an array of colors (useful for chart color arrays)
 *
 * @param colors - Array of color values (can include CSS variables)
 * @param element - Optional element to use for computing styles
 * @returns Array of resolved color values
 */
export function resolveColorArray(
  colors: (string | undefined)[] | undefined,
  element?: HTMLElement | null
): string[] {
  if (!colors || !Array.isArray(colors)) {
    return [];
  }

  return colors.map((color) => resolveCSSVariable(color, element));
}

/**
 * Resolve colors in a Recharts-compatible format
 * Handles both single colors and color arrays
 *
 * @param colors - Color value or array of colors
 * @param element - Optional element to use for computing styles
 * @returns Resolved color(s)
 */
export function resolveChartColors(
  colors: string | string[] | undefined,
  element?: HTMLElement | null
): string | string[] {
  if (!colors) {
    return '';
  }

  if (Array.isArray(colors)) {
    return resolveColorArray(colors, element);
  }

  return resolveCSSVariable(colors, element);
}

/**
 * Create a memoized color resolver for performance
 * Caches resolved colors to avoid repeated DOM queries
 */
export function createColorResolver(element?: HTMLElement | null) {
  const cache = new Map<string, string>();

  return {
    resolve: (color: string | undefined): string => {
      if (!color) return '';

      // Check cache first
      if (cache.has(color)) {
        return cache.get(color)!;
      }

      // Resolve and cache
      const resolved = resolveCSSVariable(color, element);
      cache.set(color, resolved);
      return resolved;
    },
    resolveArray: (colors: (string | undefined)[] | undefined): string[] => {
      if (!colors || !Array.isArray(colors)) return [];
      return colors.map(color => {
        if (!color) return '';
        if (cache.has(color)) return cache.get(color)!;
        const resolved = resolveCSSVariable(color, element);
        cache.set(color, resolved);
        return resolved;
      });
    },
    clear: () => cache.clear(),
  };
}
